import axios from "axios";
import { decodeJwt } from "jose";
import { profileRequestOptions } from "./profile-request";

// Public authentication requests must never inherit the protected API interceptors.
export const authApi = axios.create({ baseURL: `${process.env.REACT_APP_URL_SERVER}/api/v1/`, timeout: 30000 });
let renewal = null;
let challenge = null;
let sessionVersion = 0;
const sessionKeys = ["my_code", "refresh", "type", "first_name", "last_name", "email", "id", "elevva_role", "privacy_policy_accepted", "terms_and_conditions_accepted", "profile_pic", "membership", "company_id", "mob_api_key", "mob_integration", "mob_currency"];

export function clearSession() {
    sessionVersion += 1;
    sessionKeys.forEach(key => localStorage.removeItem(key));
    challenge = null;
}

export function saveTokens(data) {
    if (!data?.access || !data?.refresh) throw new Error("No se recibió una sesión válida. Solicita otro código o vuelve a ingresar.");
    localStorage.setItem("my_code", data.access);
    localStorage.setItem("refresh", data.refresh);
}

export async function getAccessToken(force = false) {
    const access = localStorage.getItem("my_code");
    if (!access) return null;
    let expired = true;
    try { expired = decodeJwt(access).exp * 1000 <= Date.now() + 35000; } catch { /* Renew malformed access tokens. */ }
    if (!expired && !force) return access;
    if (!renewal) {
        const version = sessionVersion;
        renewal = authApi.post("users/auth/login/refresh", { refresh: localStorage.getItem("refresh") })
            .then(({ data }) => {
                if (version !== sessionVersion) throw new Error("La sesión cambió. Vuelve a ingresar.");
                saveTokens(data);
                return data.access;
            }).catch(error => {
                if ([400, 401, 403].includes(error.response?.status) && version === sessionVersion) clearSession();
                throw error;
            }).finally(() => { renewal = null; });
    }
    return renewal;
}

export async function sessionGet(url) {
    let access = await getAccessToken();
    if (!access) throw new Error("Tu sesión terminó. Vuelve a ingresar.");
    try {
        return await authApi.get(url, { ...profileRequestOptions(url), headers: { Authorization: `Bearer ${access}` } });
    } catch (error) {
        if (error.response?.status !== 401) throw error;
        access = await getAccessToken(access === localStorage.getItem("my_code"));
        try {
            return await authApi.get(url, { ...profileRequestOptions(url), headers: { Authorization: `Bearer ${access}` } });
        } catch (retryError) {
            if (retryError.response?.status === 401) clearSession();
            throw retryError;
        }
    }
}

export async function loadCorporateSession(context = "login") {
    const version = sessionVersion;
    const assertCurrent = () => { if (version !== sessionVersion) throw new Error("La sesión cambió. Vuelve a ingresar."); };
    const { data: user } = await sessionGet("users/current/");
    assertCurrent();
    if (!user.is_corporate || ![10, 20, 30].includes(Number(user.role))) {
        await logout(false);
        throw new Error("Esta cuenta no tiene acceso al panel corporativo.");
    }
    const { data: company } = await sessionGet("memberships/corp/company");
    assertCurrent();
    const values = {
        type: "Usuario", first_name: user.first_name, last_name: user.last_name,
        email: user.email, id: user.id, elevva_role: user.role,
        privacy_policy_accepted: user.privacy_policy_accepted,
        terms_and_conditions_accepted: user.terms_and_conditions_accepted,
        profile_pic: user.customer?.profile_pic, membership: user.membership?.membership?.id,
        company_id: company.id, mob_api_key: company.mob_api_key,
        mob_integration: company.mob_integration, mob_currency: company.currency,
    };
    Object.entries(values).forEach(([key, value]) => {
        if (value == null) localStorage.removeItem(key);
        else localStorage.setItem(key, String(value));
    });
    return context === "signup" ? "/auth/welcome" : Number(user.role) === 30 ? "/dashboard/mob/booking" : "/dashboard/home";
}

export function setChallenge(email, data, context = "login") {
    if (!data?.challenge_id) throw new Error("No se recibió el desafío. Solicita un código para continuar.");
    challenge = { email, context, id: data.challenge_id,
        expiresAt: Date.now() + Number(data.expires_in ?? 300) * 1000,
        retryAt: Date.now() + Number(data.retry_after ?? 60) * 1000 };
    return challenge;
}
export const getChallenge = () => challenge;
export const clearChallenge = () => { challenge = null; };
export const requestOtp = email => authApi.post("users/auth/otp/request/", { email });
export const loginPassword = (email, password) => authApi.post("users/auth/login/", { email, password });
export const loginOtp = (id, code) => authApi.post("users/auth/login/", { challenge_id: id, code });

export function retryAfter(error) {
    const value = error.response?.headers?.["retry-after"];
    if (value == null) return error.response?.status === 429 ? 60 : 0;
    return Math.max(0, Number.isFinite(Number(value)) ? Number(value) : Math.ceil((Date.parse(value) - Date.now()) / 1000)) || 0;
}

export function authError(error) {
    if (!error.response) return error.message && !error.isAxiosError ? error.message : "No pudimos conectar con el servidor. Revisa tu conexión e intenta de nuevo.";
    if (error.response.status >= 500) return "El servicio no está disponible en este momento. Intenta más tarde.";
    const flatten = value => typeof value === "string" ? value : Array.isArray(value) ? value.map(flatten).join(" ") : value && typeof value === "object" ? Object.entries(value).map(([key, item]) => `${["detail", "error", "non_field_errors"].includes(key) ? "" : `${key}: `}${flatten(item)}`).join(" ") : "";
    return flatten(error.response.data) || "No se pudo completar la solicitud.";
}

export async function logout(redirect = true) {
    try {
        const access = await getAccessToken();
        if (access) await authApi.post("users/auth/logout/", { refresh_token: localStorage.getItem("refresh") }, { headers: { Authorization: `Bearer ${access}` } });
    } catch { /* Local logout must also work offline. */ }
    finally {
        clearSession();
        if (redirect) window.location.assign("/");
    }
}
